<template>
  <button
    v-if="isVisible"
    type="button"
    class="free-tier-pill"
    aria-haspopup="dialog"
    :aria-expanded="sheetOpen ? 'true' : 'false'"
    @click="openUpgradeOptions"
  >
    <span class="ledger-header">
      <span class="ledger-plan">{{ $t('pricing.planNames.free') }}</span>
      <!-- The only rendering of `viewPlans`. It used to be a visually-hidden
           span (the chevron was the sighted user's whole explanation); making
           it visible AND keeping the hidden copy would make a screen reader
           announce the action twice, so the hidden one is gone. -->
      <span class="ledger-link">
        <span>{{ $t('freeTierPill.viewPlans') }}</span>
        <font-awesome-icon
          icon="chevron-right"
          class="ledger-link-chevron"
          aria-hidden="true"
        />
      </span>
    </span>

    <span class="ledger-row ledger-row-chats">
      <span v-if="chatDots.length" class="ledger-dots" aria-hidden="true">
        <span
          v-for="(isSpent, index) in chatDots"
          :key="`chat-${index}`"
          class="ledger-dot"
          :class="{ 'ledger-dot-spent': isSpent }"
        />
      </span>
      <span class="ledger-count">
        {{ $t('freeTierPill.chatsLeft', { n: chatsLeft, m: limits.chats }) }}
      </span>
    </span>

    <span class="ledger-row ledger-row-lessons">
      <span v-if="lessonDots.length" class="ledger-dots" aria-hidden="true">
        <span
          v-for="(isSpent, index) in lessonDots"
          :key="`lesson-${index}`"
          class="ledger-dot"
          :class="{ 'ledger-dot-spent': isSpent }"
        />
      </span>
      <span class="ledger-count">
        {{ $t('freeTierPill.lessonsLeft', { n: lessonsLeft, m: limits.lessons }) }}
      </span>
    </span>
  </button>
</template>

<script>
/*
 * The free-tier usage ledger card (spec 2026-08-03 render D, restyled to the
 * ledger card by spec 2026-08-06 render B). The component and its root class
 * keep the "pill" name from the first spec; only the surface changed.
 *
 * WHY IT EXISTS: today a free user learns the sample is finite only when it
 * runs out. This is the standing, undramatic answer to "how much is left".
 *
 * TONE IS A CONSTRAINT, NOT A STYLE PREFERENCE. The founder stance
 * (docs/superpowers/specs/2026-07-23-motivation-initiative-roadmap.md) rules out
 * urgency: no countdown, no loss-aversion framing, and deliberately NO warning
 * state as the count approaches zero. A spent sample renders identically to a
 * full one; the numbers change and nothing else does. `carries no urgency
 * styling when the sample is spent` in the test file pins that.
 *
 * NON-OVERLAP WITH UsageMeter.vue. The two look adjacent and are not:
 *   - UsageMeter is the PAID tier's MONTHLY meter, lives on AccountView only,
 *     hides itself for free accounts, reads fetchMonthlyUsage, and shows a
 *     reset date because the allowance renews.
 *   - This pill is the FREE tier's LIFETIME sample, lives on the two views
 *     where the sample is spent, hides itself for paid accounts, reads the
 *     lifetime counts its host already fetched, and has no reset date because
 *     the sample never renews.
 * They are mutually exclusive by audience, so no account ever sees both, and
 * neither is a generalization of the other. What they DO share is the counting
 * and entitlement derivation in useUsageCounts.js, which is where a fix has to
 * go so the pill, the AccountView free block and the limit gates cannot drift.
 *
 * COUNTS ARE PASSED IN, NOT FETCHED. Both hosts already run a fan-read over
 * dialogs/{uid} for their own limit check; fetching again here would double a
 * read that is O(all dialogs) and could disagree with the gate the user is
 * standing in front of. The hosts hand down the exact usage object their own
 * verdict was computed from, so the pill and the wall can never contradict
 * each other.
 *
 * THE TAP IS AN EMIT, NOT A NAVIGATION (spec 2026-08-06). It used to push
 * /dashboard/account#upgrade-options, which threw the reader onto a different
 * tab mid-task to read a price list. Now each host mounts the plans sheet
 * itself, fed by the billing state it already owns, and this card only says
 * that the reader asked for it. The sheet's open state comes back down as
 * `sheetOpen` because `aria-expanded` has to describe a dialog this component
 * does not own; a hardcoded value would misreport it on every render.
 */
import { computed } from 'vue';
import {
  getEffectiveFreeLimits,
  isFreeTierAccount,
} from '@/composables/useUsageCounts.js';
import { useUpsellEvents } from '@/composables/useUpsellEvents.js';

export default {
  name: 'FreeTierUsagePill',
  emits: ['view-plans'],
  props: {
    // The `users/{uid}` snapshot value. Decides both whether this account is
    // on the free tier and what its effective (freebies-aware) ceilings are.
    userData: {
      type: Object,
      default: null,
    },
    // Lifetime dialog counts `{chats, lessons}` from the host's own
    // useUsageCounts read. Null until the host has them, which keeps the pill
    // from flashing a full sample before the real numbers land.
    usage: {
      type: Object,
      default: null,
    },
    // Whether the host's plans sheet is currently open, for `aria-expanded`.
    // Optional so a host that mounts no sheet is not forced to lie.
    sheetOpen: {
      type: Boolean,
      default: false,
    },
    // Which surface this card is standing on, for the upsell funnel's
    // `{ source }` dimension. Required rather than route-sniffed: reading the
    // route from here would couple a presentational component to routing and
    // silently mislabel the event the day either host is reused or nested.
    source: {
      type: String,
      required: true,
      validator: (value) => ['chat', 'course'].includes(value),
    },
  },
  setup(props, { emit }) {
    const upsellEvents = useUpsellEvents();

    const limits = computed(() => getEffectiveFreeLimits(props.userData));

    // Both free tests, ANDed -- see isFreeTierAccount. The sample-complete
    // screens on the same two views read the identical helper, so the pill and
    // the wall below it can never disagree about whether this is a free sample.
    const isFreeUser = computed(() => isFreeTierAccount(props.userData));

    const isVisible = computed(
      () =>
        isFreeUser.value &&
        props.usage != null
    );

    // Floored at zero: the server counts every dialog in the window, so a
    // grant that lapses or a race with the gate can put usage above the
    // ceiling, and "-2 left" is not a thing to show anyone.
    const remaining = (limit, used) => Math.max(0, limit - (used ?? 0));
    const chatsLeft = computed(() =>
      remaining(limits.value.chats, props.usage?.chats)
    );
    const lessonsLeft = computed(() =>
      remaining(limits.value.lessons, props.usage?.lessons)
    );

    // Dots are inventory, not a progress bar, so they only pay for themselves
    // in the middle of the range. At m = 1 (the default lessons ceiling,
    // pricing.js) a single bullet says nothing the count does not; above 6 the
    // row stops being glanceable, and freebies can raise a ceiling well past
    // that (useUsageCounts caps one grant at 10, so 13 is reachable). Rows
    // outside the range are text-only, which is why the default card shows
    // three dots over a dot-less lessons row.
    const DOTS_MIN = 2;
    const DOTS_MAX = 6;

    // One entry per unit of the ceiling, `true` where the unit is spent, so
    // the remaining units read left to right. Empty array = render text only.
    const dotsFor = (limit, left) => {
      if (!Number.isInteger(limit) || limit < DOTS_MIN || limit > DOTS_MAX) {
        return [];
      }
      return Array.from({ length: limit }, (_, index) => index >= left);
    };

    const chatDots = computed(() => dotsFor(limits.value.chats, chatsLeft.value));
    const lessonDots = computed(() =>
      dotsFor(limits.value.lessons, lessonsLeft.value)
    );

    // The tap event fires HERE rather than in the host, so it records the
    // intent even if a host ignored the emit or was mid-teardown. The host
    // fires the paired `upsell_sheet_open` when the dialog actually mounts; a
    // gap between the two counts is a real defect, and only separate events
    // can show it.
    const openUpgradeOptions = () => {
      upsellEvents.trackPillTap({ source: props.source });
      emit('view-plans');
    };

    return {
      isVisible,
      limits,
      chatsLeft,
      lessonsLeft,
      chatDots,
      lessonDots,
      openUpgradeOptions,
    };
  },
};
</script>

<style scoped>
/* Neutral surface tokens throughout: no warm accent, no semantic warning
   color. DESIGN.md reserves orange for celebration/upgrade emphasis, and this
   is an inventory readout, not a prompt.

   A raised card, not the old sunken pill: the sunken strip read as a
   placeholder, and at ~38px it sat under DESIGN.md's 44px touch floor. The
   480px cap lives here rather than in either host, so both tabs render the
   same card; FreeChatView's :deep() override was deleted with this change.
   No max-width media query any more either — the rows wrap on their own, so
   there is nothing left for a breakpoint to rearrange. */
.free-tier-pill {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 480px;
  margin-inline: auto;
  padding: 12px 14px;
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-md);
  background: var(--al-surface-raised);
  box-shadow: var(--al-shadow-sm);
  color: var(--al-text-muted);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
}

/* Hover is a capability, not a device class: on a touch WebView `:hover`
   latches onto the last-tapped element and keeps painting it hovered long
   after the finger is gone (the #527 regression; tests/unit/
   hoverCapabilityGuards.test.js scans this file for exactly that). */
@media (hover: hover) and (pointer: fine) {
  .free-tier-pill:hover {
    border-color: var(--al-primary);
  }
  .free-tier-pill:hover .ledger-count {
    color: var(--al-text);
  }
}

.free-tier-pill:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

/* The press affordance has to stay OUTSIDE the hover guard: touch devices get
   no hover, so this is their only feedback that the card is a control. */
.free-tier-pill:active {
  box-shadow: none;
}

.ledger-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.ledger-plan {
  font-weight: 600;
  color: var(--al-text);
}

/* --al-text-link, not a primary hue directly: neither hue clears AA on both
   themes (see the token's comment in utilities.css). */
.ledger-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--al-text-link);
  font-weight: 600;
  white-space: nowrap;
}

.ledger-link-chevron {
  font-size: 0.75em;
}

/* Right-aligned on the count string so the two rows line up whether or not
   they carry dots, and so a long locale wraps the string rather than
   truncating it. */
.ledger-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.ledger-count {
  color: var(--al-text-muted);
  font-variant-numeric: tabular-nums;
}

.ledger-dots {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ledger-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--al-radius-pill);
  background: var(--al-primary);
  flex: none;
}

/* Depletion changes the FILL and never the hue: a spent sample has to render
   as calmly as a full one (the founder no-urgency stance, pinned by
   `keeps the dot hue invariant between remaining and spent`). The ring is the
   same primary hue at ~3.4:1 on white and ~4.8:1 on --al-ink-800, so it clears
   the 3:1 non-text floor in both themes. */
.ledger-dot-spent {
  background: transparent;
  box-shadow: inset 0 0 0 1.5px var(--al-primary);
}
</style>
